import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { PipelineOverview } from "@/components/PipelineOverview";
import { api, publicUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success("Link copied"));

const Stat = ({ label, value, testid }) => (
  <div className="card-soft stat-card p-7 lift">
    <p className="text-xs tracking-[0.14em] uppercase text-muted-ink">{label}</p>
    <p className="font-display text-teal text-4xl font-semibold mt-3" data-testid={testid}>{value ?? "–"}</p>
  </div>
);

const QuickLink = ({ to, title, body, testid }) => (
  <Link to={to} className="card-soft p-7 lift block group" data-testid={testid}>
    <p className="font-display text-navy text-lg font-semibold">{title} <span className="inline-block transition-transform group-hover:translate-x-1">→</span></p>
    <p className="text-muted-ink mt-2 leading-relaxed">{body}</p>
  </Link>
);

export default function Dashboard() {
  const { church } = useAuth();
  const [stats, setStats] = useState(null);
  const [qr, setQr] = useState(false);
  const url = publicUrl(church.slug);
  const urlEs = `${url}?lang=es`;

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)); }, []);

  const downloadQr = () => {
    const a = document.createElement("a");
    a.href = document.getElementById("qr-canvas").toDataURL("image/png");
    a.download = `${church.slug}${qr === urlEs ? "-es" : ""}-assessment-qr.png`;
    a.click();
  };

  return (
    <div className="space-y-6">
      <section className="card-soft p-8 md:p-12 fade-up">
        <p className="eyebrow">Welcome back</p>
        <h1 className="font-display text-navy text-4xl sm:text-5xl font-semibold tracking-tight mt-3" data-testid="dashboard-church-name">{church.name}</h1>
        <p className="text-muted-ink text-lg mt-4">
          This is your church's dashboard. Volunteers go to your public assessment URL below. Results show up under{" "}
          <Link to="/volunteers" className="text-teal underline underline-offset-4">Volunteers</Link>.
        </p>
        <div className="bg-paper rounded-xl p-6 md:p-8 mt-8">
          <p className="text-xs tracking-[0.12em] uppercase text-muted-ink">Your public assessment URL</p>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <code className="font-mono-dt text-navy text-base md:text-lg break-all" data-testid="public-url">{url}</code>
            <div className="flex gap-3">
              <button className="chip-btn" onClick={() => copy(url)} data-testid="copy-url-btn">Copy</button>
              <button className="chip-btn" onClick={() => setQr(url)} data-testid="qr-btn">QR code</button>
              <a className="chip-btn" href={url} target="_blank" rel="noreferrer" data-testid="open-url-btn">Open</a>
            </div>
          </div>
          <p className="text-sm text-muted-ink mt-3">Share this link with volunteers. {church.notify_enabled ? <>Each completion is emailed to <strong className="text-navy">{church.notify_email}</strong>, and v</> : "V"}olunteers get their own copy with a PDF.</p>
          {church.billing?.pro && <div className="border-t border-line mt-6 pt-6">
            <p className="text-xs tracking-[0.12em] uppercase text-muted-ink">Spanish link · Enlace en español</p>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <code className="font-mono-dt text-navy text-base md:text-lg break-all" data-testid="public-url-es">{urlEs}</code>
              <div className="flex gap-3">
                <button className="chip-btn" onClick={() => copy(urlEs)} data-testid="copy-url-es-btn">Copy</button>
                <button className="chip-btn" onClick={() => setQr(urlEs)} data-testid="qr-es-btn">QR code</button>
                <a className="chip-btn" href={urlEs} target="_blank" rel="noreferrer" data-testid="open-url-es-btn">Open</a>
              </div>
            </div>
            <p className="text-sm text-muted-ink mt-3">Same assessment, entirely in Spanish: questions, results, email and PDF. Volunteers can also switch languages on the first screen.</p>
          </div>}
        </div>
      </section>

      <div className="grid md:grid-cols-3 gap-6">
        <Stat label="Assessments" value={stats?.volunteers} testid="stat-volunteers" />
        <Stat label="Assessments this month" value={stats?.this_month} testid="stat-month" />
        <Stat label="Teams configured" value={stats?.teams} testid="stat-teams" />
      </div>

      {church?.billing?.pro && <PipelineOverview />}

      <div className="grid md:grid-cols-3 gap-6">
        <QuickLink to="/volunteers" title="Volunteers" body="See who has completed an assessment and where they fit." testid="quick-volunteers" />
        <QuickLink to="/teams" title="Teams" body="Edit your ministry teams and the profile each one is tuned for." testid="quick-teams" />
        <QuickLink to="/settings" title="Settings" body="Church name, link, and where results are emailed." testid="quick-settings" />
      </div>

      {stats?.recent?.length > 0 && (
        <section className="card-soft p-8">
          <h2 className="font-display text-navy text-xl font-semibold">Recent completions</h2>
          <div className="divide-y divide-[var(--line)] mt-4">
            {stats.recent.map((v) => (
              <Link key={v.volunteer_id} to={`/volunteers/${v.volunteer_id}`} className="flex items-center justify-between py-4 hover:text-teal transition-colors" data-testid={`recent-${v.volunteer_id}`}>
                <span className="font-medium">{v.name}</span>
                <span className="text-sm text-muted-ink">{v.disc_profile} · {v.matches?.[0]?.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Dialog open={!!qr} onOpenChange={(o) => !o && setQr(false)}>
        <DialogContent className="max-w-sm" data-testid="qr-dialog">
          <DialogHeader><DialogTitle className="font-display">Assessment QR code{qr === urlEs ? " (Español)" : ""}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-5 py-4">
            <QRCodeCanvas id="qr-canvas" value={qr || url} size={220} fgColor="#0F2A44" includeMargin />
            <button className="btn-navy" onClick={downloadQr} data-testid="download-qr-btn">Download PNG</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
